/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable react/jsx-no-bind */
import React, { memo, useCallback, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import { capitalize, isEmpty } from 'lodash';

import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import MUITextField from '@material-ui/core/TextField';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import RadioGroup from '@material-ui/core/RadioGroup';

import { openDashboardDialog } from '@actions/dashboard-actions';
import {
  clearSchedule,
  cloneSegment,
  deleteSegment,
  setActiveLocation,
  swapSegmentFields,
  updatePolygon,
  updateSegmentEndpoints,
  updateSegmentField
} from '@actions/data-detail-actions';

import { colors, dotmapsBlack80 } from '@constants/colors';
import { displaySegmentField, displaySegmentType, getEntityTypeLabel, getSegmentFieldConfig } from '@constants/config';
import * as dialog from '@constants/dialogs';
import { detailEdit, formStyles } from '@constants/mui-theme';

import DataTypesSelect from '@forms/data-types-select';
import InputAutocomplete from '@forms/input-autocomplete';
import ShapeTypeTooltip from '@forms/shape-type-tooltip';
import SnapTooltip from '@forms/snap-tooltip';

import { Button, Checkbox, Icon, IconButton, Radio } from '@mui';

import {
  getSegmentTitle,
  renderScheduleString,
  renderWarning,
  showMissingDatesWarning,
  showScheduleExceedsWarning
} from '@utils/segment-schedule/common';

import './segment-list.scss';

const TextField = withStyles({
  root: {
    '& input.Mui-disabled, & textarea.Mui-disabled': {
      color: colors.dotmapsBlack40,
      WebkitTextFillColor: colors.dotmapsBlack40
    },
    '& label.Mui-disabled': {
      color: colors.dotmapsBlack40
    }
  }
})(MUITextField);

const useRadioStyles = makeStyles(() => ({
  label: {
    fontFamily: 'Roboto',
    fontSize: '14px',
    lineHeight: '1.29',
    color: dotmapsBlack80
  },
  labelDisabled: {
    fontFamily: 'Roboto',
    fontSize: '14px',
    lineHeight: '1.29',
    color: 'rgba(0,0,0,0.3)'
  }
}));

// Convert common props to TextField props
const toTextFieldProps = ({
  errorText,
  errorStyle,
  floatingLabelText,
  floatingLabelStyle,
  floatingLabelShrinkStyle, // eslint-disable-line no-unused-vars
  inputStyle, // eslint-disable-line no-unused-vars
  textareaStyle, // eslint-disable-line no-unused-vars
  underlineDisabledStyle, // eslint-disable-line no-unused-vars
  underlineStyle, // eslint-disable-line no-unused-vars
  underlineFocusStyle, // eslint-disable-line no-unused-vars
  onChange,
  style,
  ...rest
}) => ({
  ...rest,
  error: Boolean(errorText),
  helperText: errorText || null,
  ...(errorStyle && { FormHelperTextProps: { style: errorStyle } }),
  label: floatingLabelText,
  ...(floatingLabelStyle && { InputLabelProps: { style: floatingLabelStyle } }),
  onChange: onChange ? event => onChange(event, event.target.value) : null,
  style: { marginTop: '1rem', marginBottom: '1rem', ...style }
});

const MAX_TITLE_LEN = 50;
const MAX_DESCRIPTION_LEN = 200;

const SegmentEditListItem = ({
  checkAddressErrors,
  data,
  dataType,
  onClick,
  readOnly
}) => {
  const dispatch = useDispatch();
  const segments = useSelector(state => state.dataDetail.data.segments);
  const {
    activeSegmentId,
    activeStart,
    activeEnd,
    activeType
  } = useSelector(state => state.dataDetail.activeLocation);

  const radioClasses = useRadioStyles();

  const segment = useMemo(() => segments?.find(seg => seg.id === activeSegmentId), [activeSegmentId, segments]);

  const onShapeTypeChange = useCallback(
    (event, type) => {
      dispatch(setActiveLocation({ activeType: type }));
      if (type === 'Point') {
        dispatch(updateSegmentEndpoints({ start: activeStart, end: null }, activeSegmentId, segment.override_street_centering));
        dispatch(setActiveLocation({ activeEnd: null }));
      }
      if (type === 'LineString' && activeEnd) {
        dispatch(updateSegmentEndpoints({ start: activeStart, end: activeEnd }, activeSegmentId, segment.override_street_centering));
      }
      if (type === 'Polygon') {
        dispatch(updateSegmentEndpoints({ start: null, end: null }, activeSegmentId, segment.override_street_centering));
      }
    },
    [activeStart, activeEnd, activeSegmentId, dispatch, segment.override_street_centering]
  );

  const onFieldChange = useCallback(
    (name, value) => {
      dispatch(updateSegmentField(segment.id, name, value));
      if (name === 'override_street_centering' && segment.id === activeSegmentId) {
        dispatch(updateSegmentEndpoints({ start: activeStart, end: activeEnd }, activeSegmentId, value));
      }
    },
    [activeSegmentId, activeStart, activeEnd, dispatch, segment.id]
  );

  const onActiveSegmentChanged = useCallback(
    endPoints => {
      const newEndPoints = { ...{ start: activeStart, end: activeEnd }, ...endPoints };
      dispatch(setActiveLocation({
        activeStart: newEndPoints.start,
        activeEnd: newEndPoints.end
      }));
      dispatch(updateSegmentEndpoints(newEndPoints, activeSegmentId, segment.override_street_centering, 'segmentForm'));
    },
    [activeSegmentId, activeStart, activeEnd, dispatch, segment.override_street_centering]
  );

  const onFieldError = useCallback(
    (name, error) => {
      dispatch(updateSegmentField(segment.id, name, null, error));
    },
    [dispatch, segment.id]
  );

  const onCheck = useCallback(
    event => {
      const name = event.target.name;
      onFieldChange(name, !segment[name]);
    },
    [onFieldChange, segment]
  );

  const onFromChange = useCallback(value => onFieldChange('display_from', value), [onFieldChange]);

  const onToChange = useCallback(value => onFieldChange('display_to', value), [onFieldChange]);

  const onTitleChange = useCallback((event, value) => onFieldChange('title', value.substring(0, MAX_TITLE_LEN)), [onFieldChange]);

  const onDescriptionChange = useCallback((event, value) => onFieldChange('description', value.substring(0, MAX_DESCRIPTION_LEN)), [onFieldChange]);

  const onDirectionChange = useCallback((value) => onFieldChange('direction', value), [onFieldChange]);

  const onImpactChange = useCallback((value) => onFieldChange('impact_type', value), [onFieldChange]);

  const onStatusChange = useCallback((value) => onFieldChange('status', value), [onFieldChange]);

  const onFromGeocode = useCallback(latLng => onActiveSegmentChanged({ start: latLng }), [onActiveSegmentChanged]);

  const onToGeocode = useCallback(latLng => onActiveSegmentChanged({ end: latLng }), [onActiveSegmentChanged]);

  const onFromGeocodeError = useCallback(status => onFieldError('display_from', status), [onFieldError]);

  const onToGeocodeError = useCallback(status => onFieldError('display_to', status), [onFieldError]);

  const swapAddresses = useCallback(
    () => {
      dispatch(swapSegmentFields(segment.id, 'display_from', 'display_to'));
      // Swap start/end too:
      dispatch(setActiveLocation({
        activeStart: activeEnd,
        activeEnd: activeStart
      }));
    },
    [activeStart, activeEnd, dispatch, segment.id]
  );

  const onClone = useCallback(() => {
    dispatch(cloneSegment(segment.id));
  }, [dispatch, segment.id]);

  const onDeleteSegment = useCallback(() => {
    dispatch(deleteSegment(segment.id));
  }, [dispatch, segment.id]);

  const editSchedule = useCallback(
    () => {
      const params = {
        id: segment.id,
        start_date: data.start_date,
        end_date: data.end_date
      };
      dispatch(openDashboardDialog(dialog.EDIT_SCHEDULE, params));
    },
    [data.start_date, data.end_date, dispatch, segment.id]
  );

  const onClearSchedule = useCallback(() => {
    dispatch(clearSchedule(segment.id));
  }, [dispatch, segment.id]);

  const clearPolygon = useCallback(() => {
    dispatch(updatePolygon(segment.id, null, 'map'));
  }, [dispatch, segment.id]);

  const renderShapeType = useCallback(() => {
    // It could be simpler to have in this method a single RadioButtonGroup
    // and only switch on/off the polygon RadioButton, however, for
    // some reason (some code inside RadioButtonGroup), we cannot
    // add conditional JSX within RadioButtonGroups, thus we
    // have to separate this in two.
    if (!displaySegmentField(dataType, 'shape-type')) {
      return null;
    }
    const radioLabelClass = readOnly ? radioClasses.labelDisabled : radioClasses.label;
    if (displaySegmentType(dataType, 'polygon')) {
      return (
        <div styleName="shape-type">
          <RadioGroup
            name="shapeType"
            defaultValue={activeType}
            value={activeType}
            onChange={onShapeTypeChange}
            row
          >
            <FormControlLabel
              disabled={readOnly}
              value="Point"
              control={<Radio color="primary" />}
              label={<div styleName="label">Point</div>}
              style={{ paddingRight: '1rem' }}
              classes={{ label: radioLabelClass }}
            />
            <FormControlLabel
              disabled={readOnly}
              value="LineString"
              control={<Radio color="primary" />}
              label={<div styleName="label">Segment</div>}
              style={{ paddingRight: '1rem' }}
              classes={{ label: radioLabelClass }}
            />
            <FormControlLabel
              disabled={readOnly}
              value="Polygon"
              control={<Radio color="primary" />}
              label={<div styleName="label">Polygon</div>}
              style={{ paddingRight: '1rem' }}
              classes={{ label: radioLabelClass }}
            />
          </RadioGroup>
          <ShapeTypeTooltip tabIndex="0" />
        </div>
      );
    }
    return (
      <div styleName="shape-type">
        <RadioGroup
          name="shapeType"
          defaultValue={activeType}
          value={activeType}
          onChange={onShapeTypeChange}
          row
        >
          <FormControlLabel
            disabled={readOnly}
            value="Point"
            control={<Radio color="primary" />}
            label={<div styleName="label">Point</div>}
            style={{ paddingRight: '1rem' }}
            classes={{ label: radioLabelClass }}
          />
          <FormControlLabel
            disabled={readOnly}
            value="LineString"
            control={<Radio color="primary" />}
            label={<div styleName="label">Segment</div>}
            style={{ paddingRight: '1rem' }}
            classes={{ label: radioLabelClass }}
          />
        </RadioGroup>
        <ShapeTypeTooltip />
      </div>
    );
  }, [activeType, dataType, onShapeTypeChange, radioClasses.label, radioClasses.labelDisabled, readOnly]);

  const renderAddress = useCallback(() => {
    if (activeType === 'Polygon') {
      return null;
    }
    if (!displaySegmentField(dataType, 'addresses')) {
      return null;
    }
    const fromName = activeType === 'LineString' ? 'From address*' : 'Address*';
    const toName = 'To address*';
    let fromError = segment.display_from_error;
    let toError = segment.display_to_error;
    if (checkAddressErrors &&
       (isEmpty(segment.display_from) ||
       isEmpty(segment.display_to))) {
      fromError = 'Enter an address';
      toError = 'Enter an address';
    }
    return (
      <div styleName="address">
        <div styleName="from-to">
          <div>
            <InputAutocomplete
              initName="placeFrom"
              inputProps={{
                disabled: readOnly,
                errorText: fromError,
                floatingLabelText: fromName,
                name: fromName,
                onChange: onFromChange,
                value: segment.display_from || '',
                ...detailEdit.segmentEdit.textField
              }}
              onGeocodeCompleted={onFromGeocode}
              onGeocodeError={onFromGeocodeError}
            />
          </div>
          {activeType === 'LineString' &&
            <div>
              <InputAutocomplete
                initName="placeTo"
                inputProps={{
                  disabled: readOnly,
                  errorText: toError,
                  floatingLabelText: toName,
                  onChange: onToChange,
                  value: segment.display_to || '',
                  ...detailEdit.segmentEdit.textField
                }}
                onGeocodeCompleted={onToGeocode}
                onGeocodeError={onToGeocodeError}
              />
            </div>
          }
        </div>
        {activeType === 'LineString' && !readOnly &&
          <div styleName="swap">
            <IconButton onClick={swapAddresses} tooltip="Swap from/to" aria-label="Swap from/to">
              <Icon role="img">swap_vert</Icon>
            </IconButton>
          </div>
        }
      </div>
    );
  }, [
    activeType,
    checkAddressErrors,
    dataType,
    onFromChange,
    onFromGeocode,
    onFromGeocodeError,
    onToChange,
    onToGeocode,
    onToGeocodeError,
    readOnly,
    segment,
    swapAddresses
  ]);

  const renderSnapAndAlley = useCallback(() => {
    if (activeType === 'Polygon') {
      return null;
    }
    if (!displaySegmentField(dataType, 'snap_n_alley')) {
      return null;
    }
    const segmentFieldConfig = getSegmentFieldConfig(dataType, 'snap_n_alley');
    return (
      <div styleName="snap-n-alley">
        <FormControlLabel
          control={
            <Switch
              name="override_street_centering"
              disabled={readOnly}
              checked={!segment.override_street_centering}
              onChange={onCheck}
              color="primary"
            />
          }
          label={'label' in segmentFieldConfig ? segmentFieldConfig.label : 'Snap to streets'}
          disabled={readOnly}
          style={formStyles(readOnly).toggle.style}
        />
        <SnapTooltip />
        {segment.override_street_centering && !('hide_alley_toggle' in segmentFieldConfig) &&
          <span style={{ paddingLeft: '1.5rem' }}>
            <Checkbox
              checked={(segment.override_street_centering || false) && (segment.is_alley || false)}
              disabled={!segment.override_street_centering || readOnly}
              label="This is an alley"
              name="is_alley"
              onChange={onCheck}
              size="small"
              style={{ padding: '0 10px 0 10px' }}
            />
          </span>
        }
      </div>
    );
  }, [
    activeType,
    dataType,
    onCheck,
    readOnly,
    segment
  ]);

  const renderImpact = useCallback(() => {
    if (!displaySegmentField(dataType, 'impact')) {
      return null;
    }
    return (
      <div styleName="impact">
        <DataTypesSelect
          dataName="segment_direction"
          disabled={readOnly}
          floatingLabelText="Direction of impact"
          name="direction"
          onChange={onDirectionChange}
          style={{ margin: '1rem 1rem 0 0', width: '100%' }}
          styleName="direction"
          value={segment.direction}
        />
        <DataTypesSelect
          dataName="segment_impact_type"
          disabled={readOnly}
          floatingLabelText="Type of impact"
          name="impact_type"
          onChange={onImpactChange}
          style={{ margin: '1rem 0 0 0', width: '100%' }}
          styleName="impact-type"
          value={segment.impact_type}
        />
      </div>
    );
  }, [
    dataType, onDirectionChange, onImpactChange, readOnly, segment
  ]);

  const renderStatus = useCallback(() => {
    if (!displaySegmentField(dataType, 'status')) {
      return null;
    }
    return (
      <div styleName="impact">
        <DataTypesSelect
          dataName="segment_status"
          disabled={readOnly}
          floatingLabelText="Status"
          name="status"
          onChange={onStatusChange}
          style={{ margin: '1rem 0 0 0', width: '100%' }}
          styleName="direction"
          value={segment.status}
        />
      </div>
    );
  }, [
    dataType, onStatusChange, readOnly, segment
  ]);

  const renderTitle = useCallback(() => {
    if (!displaySegmentField(dataType, 'title')) {
      return null;
    }
    const segmentFieldConfig = getSegmentFieldConfig(dataType, 'title');
    return (
      <div styleName="title">
        <TextField
          {...toTextFieldProps({
            ...detailEdit.segmentEdit.textField,
            floatingLabelText: 'label' in segmentFieldConfig ? segmentFieldConfig.label : 'Title',
            onChange: onTitleChange
          })}
          disabled={readOnly}
          name="Title"
          value={segment.title}
        />
        <div styleName="text-field-footer">
          <div styleName="hint">
            Replaces the header with a custom title instead of On Street value.
          </div>
          <div styleName="counter">
            {segment.title ? segment.title.length : 0}/{MAX_TITLE_LEN}
          </div>
        </div>
      </div>
    );
  }, [
    dataType, onTitleChange, readOnly, segment
  ]);

  const renderDescription = useCallback(() => {
    if (!displaySegmentField(dataType, 'description')) {
      return null;
    }
    const segmentFieldConfig = getSegmentFieldConfig(dataType, 'description');
    return (
      <div styleName="description">
        <TextField
          {...toTextFieldProps({
            ...detailEdit.segmentEdit.textField,
            floatingLabelText: 'label' in segmentFieldConfig ? segmentFieldConfig.label : 'Description',
            onChange: onDescriptionChange
          })}
          disabled={readOnly}
          multiline
          name="Description"
          value={segment.description}
        />
        <div styleName="text-field-footer">
          <div styleName="hint">
            Describe any unique attributes or notes associated to selected location.
          </div>
          <div styleName="counter">
            {segment.description ? segment.description.length : 0}/{MAX_DESCRIPTION_LEN}
          </div>
        </div>
      </div>
    );
  }, [
    dataType, onDescriptionChange, readOnly, segment
  ]);

  const renderCNN = useCallback(() => {
    if (!displaySegmentField(dataType, 'cnn')) {
      return null;
    }
    return (
      <div styleName="description">
        <TextField
          {...toTextFieldProps({
            ...detailEdit.segmentEdit.textField,
            floatingLabelText: 'CNN'
          })}
          disabled={readOnly}
          multiline
          name="CNN"
          value={segment.cnn}
          InputProps={{ readOnly: true }}
          InputLabelProps={{ shrink: Boolean(segment.cnn) }}
        />
      </div>
    );
  }, [
    dataType, readOnly, segment.cnn
  ]);

  const displaySchedule = useCallback(() => displaySegmentField(dataType, 'schedule'), [dataType]);

  const renderSchedule = useCallback(() => {
    if (!displaySchedule()) {
      return null;
    }
    if (isEmpty(segment.schedules)) {
      return null;
    }
    return (
      <div styleName="schedule">
        {renderScheduleString(segment.schedules)}
        {!readOnly && !isEmpty(segment.schedules) && (
          <div styleName="schedule-edit-icon">
            <IconButton onClick={editSchedule} size="small" tooltip="Edit Schedule" aria-label="Edit Schedule">
              <Icon role="img" color={colors.opacity.black0_6}>edit</Icon>
            </IconButton>
          </div>
        )}
        {!readOnly && !isEmpty(segment.schedules) && (
          <div styleName="schedule-remove-icon">
            <IconButton onClick={onClearSchedule} size="small" tooltip="Remove Schedule" aria-label="Remove Schedule">
              <Icon role="img" color={colors.opacity.black0_6}>close</Icon>
            </IconButton>
          </div>
        )}
      </div>
    );
  }, [displaySchedule, editSchedule, onClearSchedule, readOnly, segment]);

  const polygonInfoStyle = useMemo(() => `polygon-info ${checkAddressErrors ? 'polygon-error' : ''}`, [checkAddressErrors]);

  return (
    <div styleName="segment-edit-list-item">
      <div onClick={onClick} role="presentation" styleName="action-header">
        <div styleName="label">{getSegmentTitle(segment)}</div>
        <div styleName="icon">
          <Icon role="img">unfold_less</Icon>
        </div>
      </div>
      {renderShapeType()}
      {renderAddress()}
      {renderSnapAndAlley()}
      {activeType === 'Polygon' && !segment.shape &&
        <div styleName={polygonInfoStyle}><span>Draw a polygon on the map</span></div>
      }
      {activeType === 'Polygon' && segment.shape &&
        <div styleName="polygon-info">
          <span>Polygon</span>
          {!readOnly && <Button onClick={clearPolygon}>CLEAR</Button>}
        </div>
      }
      {renderImpact()}
      {renderStatus()}
      {renderTitle()}
      {renderDescription()}
      {renderCNN()}
      {displaySchedule() && showMissingDatesWarning(data.start_date, data.end_date) &&
        <div styleName="segment-edit-warning">
          {renderWarning(`${capitalize(getEntityTypeLabel(dataType))} schedule is missing.`)}
        </div>
      }
      {displaySchedule() && showScheduleExceedsWarning(data.start_date, data.end_date, segment) &&
        <div styleName="segment-edit-warning">
          {renderWarning(`Schedule exceeds the ${getEntityTypeLabel(dataType)} schedule.`)}
        </div>
      }
      {renderSchedule()}
      {displaySchedule() && !readOnly && isEmpty(segment.schedules) &&
        <div styleName="schedule-button">
          <Button color="primary" onClick={editSchedule}>
            SET SCHEDULE
          </Button>
        </div>
      }
      {!readOnly && <div styleName="separator" />}
      {!readOnly &&
        <div styleName="action">
          <div styleName="empty" />
          <IconButton onClick={onClone} tooltip="Duplicate" style={{ opacity: '0.6' }} aria-label="Duplicate">
            <Icon role="img">content_copy</Icon>
          </IconButton>
          <IconButton onClick={onDeleteSegment} tooltip="Delete" style={{ opacity: '0.6' }} aria-label="Delete">
            <Icon role="img">delete</Icon>
          </IconButton>
        </div>
      }
    </div>
  );
};

SegmentEditListItem.propTypes = {
  // If true, check if addresses are empty and show the error.
  checkAddressErrors: PropTypes.bool,
  data: PropTypes.object,
  dataType: PropTypes.string,
  onClick: PropTypes.func,
  readOnly: PropTypes.bool
};

export default memo(SegmentEditListItem);
